<template>
    <b-modal
        :id="modalId"
        :title="isEdit ? '평가 그룹 수정' : '평가 그룹 추가'"
        centered
        no-close-on-backdrop
        :ok-title="isEdit ? '수정' : '생성'"
        cancel-title="취소"
        :no-close-on-esc="submitting"
        :hide-header-close="submitting"
        :ok-disabled="submitting"
        :cancel-disabled="submitting"
        @show="onShow"
        @hide="onHide"
        @hidden="initialForm = null"
        @ok="onOk"
    >
        <b-form @submit.stop.prevent="onOk">
            <!--
                group_code 는 프론트 빌드와 1:1 로 약속된 코드값이라 생성 시에만 입력받고
                수정 시에는 절대 바꾸지 못하도록 readonly 로 둔다 (바꾸면 연결 프로젝트가
                평가 신청 화면에서 통째로 사라진다).
            -->
            <b-form-group
                label="그룹 코드"
                label-for="pg-group-code"
                :description="isEdit
                    ? '그룹 코드는 프론트 빌드와 약속된 값이라 수정할 수 없습니다.'
                    : '프론트 빌드와 1:1로 약속된 코드값입니다. 생성 후에는 변경할 수 없습니다.'"
            >
                <b-form-input
                    id="pg-group-code"
                    v-model.trim="form.group_code"
                    :readonly="isEdit"
                    :state="isEdit ? null : (dirty.group_code ? !groupCodeError : null)"
                    placeholder="예: QESG_SUPPLY_2026"
                    autocomplete="off"
                    aria-describedby="pg-group-code-feedback"
                    @input="dirty.group_code = true"
                />
                <b-form-invalid-feedback id="pg-group-code-feedback">{{ groupCodeError }}</b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="그룹명 (표시명)" label-for="pg-group-name">
                <b-form-input
                    id="pg-group-name"
                    v-model.trim="form.group_name"
                    :state="dirty.group_name ? !!form.group_name : null"
                    placeholder="예: QESG 공급망 평가 2026"
                    autocomplete="off"
                    aria-describedby="pg-group-name-feedback"
                    @input="dirty.group_name = true"
                />
                <b-form-invalid-feedback id="pg-group-name-feedback">그룹명을 입력해 주세요.</b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                label="보고서 엔진 (report_type)"
                label-for="pg-report-type"
                description="EV_COUNT 는 v3 신규 보고서 엔진, 미지정은 v2 기본 계산식으로 처리됩니다."
            >
                <b-form-select
                    id="pg-report-type"
                    v-model="form.report_type"
                    :options="reportTypeOptions"
                />
            </b-form-group>

            <b-form-group label="활성 여부">
                <b-form-checkbox v-model="form.is_active" switch>
                    {{ form.is_active ? '활성 (선택 드롭다운에 노출)' : '비활성 (드롭다운에서 숨김)' }}
                </b-form-checkbox>
            </b-form-group>

            <b-form-group label="운영 메모 (선택)" label-for="pg-memo">
                <b-form-textarea
                    id="pg-memo"
                    v-model="form.memo"
                    rows="2"
                    max-rows="5"
                    placeholder="운영자용 메모"
                />
            </b-form-group>
        </b-form>

        <template #modal-footer="{ cancel }">
            <b-button variant="secondary" :disabled="submitting" @click="cancel()">
                취소
            </b-button>
            <b-button variant="primary" :disabled="submitting" @click="onOk($event)">
                <b-spinner v-if="submitting" small class="mr-1" />
                {{ isEdit ? '수정' : '생성' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard';

// 신청 화면 빌드 값(tenant.js clients[].value, 기본 'SAQ')이 모두 이 범위다. 공백·한글이 섞이면 빌드 값과 맞출 수 없다
const GROUP_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

export default {
    name: 'ProjectGroupFormModal',
    mixins: [unsavedChangesGuard],
    props: {
        modalId: {
            type: String,
            default: 'project-group-form-modal',
        },
        // 'create' | 'edit'
        mode: {
            type: String,
            default: 'create',
        },
        // 수정 대상 그룹 (create 모드에서는 무시)
        group: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            form: this.blankForm(),
            // 열 때의 폼 — 닫기 전 미저장 판정 기준 (닫혀 있으면 null)
            initialForm: null,
            dirty: { group_code: false, group_name: false },
            submitting: false,
            reportTypeOptions: [
                { value: null, text: '기본 계산식 (v2)' },
                { value: 'EV_COUNT', text: 'EV_COUNT (v3 신규 보고서 엔진)' },
            ],
        };
    },
    computed: {
        isEdit() {
            return this.mode === 'edit';
        },
        groupCodeError() {
            if (this.isEdit) return '';
            if (!this.form.group_code) return '그룹 코드를 입력해 주세요.';
            return GROUP_CODE_PATTERN.test(this.form.group_code) ? '' : '영문·숫자·_·- 만 쓸 수 있습니다. (공백·한글 불가)';
        },
        hasUnsavedChanges() {
            return this.initialForm != null && JSON.stringify(this.form) !== this.initialForm;
        },
    },
    methods: {
        blankForm() {
            return {
                group_code: '',
                group_name: '',
                report_type: null,
                is_active: true,
                memo: '',
            };
        },
        // 모달이 열릴 때마다 props 기준으로 폼을 초기화한다.
        onShow() {
            this.dirty = { group_code: false, group_name: false };
            this.submitting = false;
            if (this.isEdit && this.group) {
                this.form = {
                    id: this.group.id,
                    group_code: this.group.group_code || '',
                    group_name: this.group.group_name || '',
                    // 빈문자열/undefined 는 기본 계산식(null)로 정규화
                    report_type: this.group.report_type || null,
                    is_active: this.group.is_active !== false,
                    memo: this.group.memo || '',
                };
            } else {
                this.form = this.blankForm();
            }
            this.initialForm = JSON.stringify(this.form);
        },
        // X·ESC·취소·hide() 모두 이리로 온다. 저장 중이면 막고, 입력이 바뀌었으면 확인 후 닫는다
        onHide(bvEvent) {
            if (this.submitting) {
                bvEvent.preventDefault();
                return;
            }
            if (this.hasUnsavedChanges) {
                bvEvent.preventDefault();
                this.confirmDiscardChanges({message: '입력한 내용이 저장되지 않았습니다. 닫을까요?'}).then((ok) => {
                    if (!ok) return;
                    this.initialForm = null;
                    this.$bvModal.hide(this.modalId);
                });
            }
        },
        // 오류는 칸 아래 문구로 보여 준다(토스트 대신)
        validate() {
            this.dirty.group_code = true;
            this.dirty.group_name = true;
            return !this.groupCodeError && !!this.form.group_name;
        },
        async onOk(evt) {
            // b-modal 기본 닫힘을 막고 직접 제어 (성공 시에만 닫는다)
            if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
            if (this.submitting) return;
            if (!this.validate()) return;

            // 확인창을 기다리는 동안에도 닫기·중복 제출을 막는다
            this.submitting = true;
            let saved;
            try {
                if (!this.isEdit && !(await this.confirmCreate())) return;
                saved = this.isEdit ? await this.submitUpdate() : await this.submitCreate();
            } catch (e) {
                this.handleError(e);
                return;
            } finally {
                this.submitting = false;
            }
            this.$emit('saved', saved);
            this.initialForm = null;
            this.$bvModal.hide(this.modalId);
        },
        confirmCreate() {
            return this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '평가 그룹 생성',
                message: `그룹 코드 ${this.form.group_code} 는 생성 후 바꿀 수 없습니다. 프론트 빌드 값과 다르면 이 그룹 프로젝트가 신청 화면에 나오지 않습니다. 생성할까요?`,
                modalOptions: { okTitle: '생성' },
            });
        },
        async submitCreate() {
            const body = {
                group_code: this.form.group_code,
                group_name: this.form.group_name,
                report_type: this.form.report_type || null,
                is_active: this.form.is_active,
                memo: this.form.memo || '',
            };
            const { data } = await this.$axios.post('/project_code/groups', body);
            this.$EventBus.$emit('toast-success', '생성 완료', `'${data.group_name}' 그룹을 생성했습니다.`);
            return data;
        },
        async submitUpdate() {
            // group_code 는 불변이라 전송하지 않는다 (서버도 무시하지만 명시적으로 제외).
            const body = {
                group_name: this.form.group_name,
                report_type: this.form.report_type || null,
                is_active: this.form.is_active,
                memo: this.form.memo || '',
            };
            const { data } = await this.$axios.put(`/project_code/groups/${this.form.id}`, body);
            this.$EventBus.$emit('toast-success', '수정 완료', `'${data.group_name}' 그룹을 수정했습니다.`);
            return data;
        },
        handleError(e) {
            const status = e && e.response && e.response.status;
            if (status === 401) return;
            const serverMsg = this.extractError(e);
            let msg;
            if (status === 409) {
                msg = serverMsg || '이미 존재하는 그룹 코드입니다. 다른 코드를 사용해 주세요.';
            } else if (status === 400) {
                msg = serverMsg || '입력값을 확인해 주세요.';
            } else if (status === 403) {
                msg = serverMsg || '권한이 없습니다. 관리자 계정으로 로그인했는지 확인해 주세요.';
            } else if (status === 404) {
                msg = serverMsg || '해당 그룹을 찾을 수 없습니다. 목록을 새로고침해 주세요.';
            } else {
                msg = serverMsg || '처리 중 오류가 발생했습니다.';
            }
            // 예전 noAutoHide 토스트처럼 오래 남긴다(공용 토스트는 자동 닫힘만 지원)
            this.$EventBus.$emit('toast-failure', '오류', msg, false, 30000);
        },
        // 백엔드 에러 응답 필드가 프로젝트마다 혼재(error/message/msg/detail)해 견고하게 추출한다.
        extractError(e) {
            const data = e && e.response && e.response.data;
            return getErrorMessage(e, '') || (data && (data.msg || data.detail)) || '';
        },
    },
};
</script>
